import { OracleSearchResponseStepDetailsDialog } from '@modules/oracle/components/OracleSearchResponseStepDetailsDialog';
import { WisdomConversationStepQueryDetails } from '@modules/oracle/components/WisdomConversationStepQueryDetails';
import { WisdomConversationStepAnswer } from '@modules/oracle/components/WisdomConversationSteps/WisdomConversationStepAnswer';
import { WisdomConversationStepIndicator } from '@modules/oracle/components/WisdomConversationSteps/WisdomConversationStepIndicator';
import { useGetOracleStepAnalyticEventInputs } from '@modules/oracle/hooks';
import { useOracleSearchStore } from '@modules/oracle/store/oracleSearchStore';
import {
  getAssistantResponseEqlType,
  trackWisdomEvents,
} from '@modules/oracle/utils';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import {
  AssistantResponseStep,
  AssistantResponseStepAnswer,
} from 'generated/graphql';
import { useEffect, useState } from 'react';

interface WisdomConversationStepProps {
  isDisabled?: boolean;
  stepAnswer?: AssistantResponseStepAnswer;
  assistantResponseStep: AssistantResponseStep;
  hasFinalAnswer: boolean;
  promptID: string;
  isReadOnly: boolean;
  stepID: string;
}

export function WisdomConversationStep({
  assistantResponseStep,
  stepAnswer,
  isDisabled = false,
  promptID,
  hasFinalAnswer,
  isReadOnly,
  stepID,
}: WisdomConversationStepProps) {
  const [isStepAccordionExpanded, setIsStepAccordionExpanded] = useState(true);
  const [shouldOpenDetailView, setShouldOpenDetailView] = useState(false);

  const finalAnswerTokensV2 = useOracleSearchStore(
    ({ data }) =>
      data.promptByID?.[promptID].assistantResponse?.answer?.tokensV2,
  );
  const endOfFinalResponse = useOracleSearchStore(
    ({ data }) => data.promptByID?.[promptID].endOfResponse,
  );
  const error = useOracleSearchStore(
    ({ data }) => data.promptByID?.[promptID].error,
  );
  const isStepResponseArrived = !!stepAnswer?.end;
  const isResponseHalted = useOracleSearchStore(
    ({ data }) => !!data.promptByID?.[promptID]?.isResponseHalted,
  );

  const assistantResponseTokenV2List =
    !stepAnswer || stepAnswer.isSameAsAnswer
      ? finalAnswerTokensV2
      : stepAnswer?.tokensV2;

  const isAnswerLoading = stepAnswer?.isSameAsAnswer
    ? !endOfFinalResponse
    : !stepAnswer?.end;

  const isAnswerPresent = !!assistantResponseTokenV2List?.length;

  const stepAnalyticEventInputs = useGetOracleStepAnalyticEventInputs({
    promptID,
    stepID,
  });

  useEffect(() => {
    if (hasFinalAnswer) {
      setIsStepAccordionExpanded(false);
    }
  }, [hasFinalAnswer]);

  const eqlResponseType = !!assistantResponseTokenV2List
    ? getAssistantResponseEqlType(assistantResponseTokenV2List)
    : null;

  const isQuantifyOrSummaryResponse =
    eqlResponseType === 'quantify' || eqlResponseType === 'summary';

  const canExpandStepAccordion = isStepResponseArrived
    ? hasFinalAnswer &&
      !isQuantifyOrSummaryResponse &&
      !!stepAnswer?.tokensV2.length
    : false;

  const assistantResponseEqlQuery = assistantResponseTokenV2List?.find(
    (token) => token.eql?.eql,
  )?.eql?.eql;

  return (
    <Box>
      <Accordion
        disableGutters
        elevation={0}
        expanded={isStepAccordionExpanded}
        onChange={
          canExpandStepAccordion
            ? (_, isExpanded) => setIsStepAccordionExpanded(isExpanded)
            : undefined
        }
        sx={{
          py: 0,
          mt: 0,
          '.MuiAccordionSummary-root.MuiButtonBase-root:hover': {
            cursor: !canExpandStepAccordion ? 'unset' : undefined,
            userSelect: !canExpandStepAccordion ? 'unset' : undefined,
          },
        }}
      >
        <AccordionSummary
          sx={{
            px: 0,
            minHeight: 'initial',
            '& .MuiAccordionSummary-content': {
              m: 0,
              minWidth: 0,
            },
          }}
          expandIcon={
            canExpandStepAccordion ? (
              <IconButton size="small" sx={{ color: 'text.secondary' }}>
                <ExpandMoreIcon />
              </IconButton>
            ) : null
          }
        >
          <Stack minWidth={0} width="100%">
            <Stack direction="row" alignItems="center">
              <WisdomConversationStepIndicator
                isResponseArrived={isStepResponseArrived}
                isDisabled={isDisabled}
                isResponseHalted={isResponseHalted || !!error}
              />
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <Typography
                  variant="subtitle2"
                  color={isDisabled ? 'text.disabled' : undefined}
                  className="step-title"
                >
                  {assistantResponseStep.question}
                </Typography>
              </Stack>
            </Stack>
            <Box pl={5}>
              {isQuantifyOrSummaryResponse && !!assistantResponseEqlQuery && (
                <WisdomConversationStepQueryDetails
                  isReadOnly={isReadOnly}
                  eqlQuery={assistantResponseEqlQuery}
                  shouldAnimate={isAnswerLoading && !isResponseHalted}
                  onClick={
                    canExpandStepAccordion
                      ? undefined
                      : () => {
                          setShouldOpenDetailView(true);
                          trackWisdomEvents({
                            eventName: 'Wisdom - Step Query Inspected',
                            ...stepAnalyticEventInputs,
                          });
                        }
                  }
                  responseType={eqlResponseType}
                />
              )}
            </Box>
          </Stack>
        </AccordionSummary>
        {!!assistantResponseTokenV2List?.length && (
          <AccordionDetails
            sx={{
              p: 0,
              pl: 5,
              color: 'text.disabled',
            }}
          >
            <WisdomConversationStepAnswer
              assistantResponseTokenV2List={assistantResponseTokenV2List}
              promptID={promptID}
              stepID={stepID}
              isLoading={isAnswerLoading}
              hasFinalAnswer={hasFinalAnswer}
              isResponseHalted={isResponseHalted || !!error}
            />
          </AccordionDetails>
        )}
      </Accordion>
      {isAnswerPresent && shouldOpenDetailView ? (
        <OracleSearchResponseStepDetailsDialog
          stepID={stepID}
          isOpen={shouldOpenDetailView}
          onClose={() => setShouldOpenDetailView(false)}
          assistantResponseTokenV2List={assistantResponseTokenV2List}
          isReadOnly={isReadOnly}
          promptID={promptID}
          isLoading={isAnswerLoading}
          stepTitle={assistantResponseStep.question}
        />
      ) : null}
    </Box>
  );
}
